<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form
        layout="horizontal"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        @submit.prevent="onSearch"
      >
        <a-row :gutter="16" align="middle">
          <a-col :span="3">
            <a-form-item label="">
              <a-switch
                v-model:checked="isFuture"
                checked-children="Future"
                un-checked-children="Option"
              />
            </a-form-item>
          </a-col>
          <a-col :span="9">
            <a-form-item label="Type">
              <a-select
                v-if="isFuture"
                v-model:value="query.externalInstClass"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in futureInstClassList"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
              <a-select
                v-else
                v-model:value="query.externalInstClass"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in optionInstClassList"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Exchange">
              <a-select
                v-model:value="query.exchangeCode"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in exchangeList"
                  :key="item.exchName"
                  :value="item.exchName"
                >
                  {{ item.exchName }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Description">
              <a-input
                v-model:value="query.securityDesc"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Underlying">
              <a-input
                v-model:value="query.underlying"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Sec Id">
              <a-input
                v-model:value="query.secId"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Identifier Type">
              <a-select
                v-model:value="query.identType"
                allow-clear
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in identifierTypeList"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Identifier">
              <a-auto-complete
                v-model:value="query.identifier"
                :options="tickerOptions"
                @search="onSearchTicker"
                @select="onSearchTicker"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item
              :colon="false"
              :labelCol="{ span: 6, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 18 }"
            >
              <div class="page-actions">
                <a-button type="primary" @click="onSearch" :loading="loading"
                  >Search</a-button
                >
                <a-button @click="showAddModal">
                  <PlusOutlined />
                  Add
                </a-button>
                <a-button @click="showCopyModal">Copy</a-button>
              </div>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h540">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
      />
    </div>
    <AddEditFutureOptionView
      v-model:visible="addModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:security="handleUpdateSecurity"
    />
    <IdentifierView
      v-model:visible="identifierModalVisible"
      :rawData="identifierRawData"
      @update:identifiers="handleUpdateIdentifiers"
    />
  </div>
</template>

<script setup>
import { ref, defineExpose, onMounted, provide, computed } from 'vue'
// import { useStore } from 'vuex'
import { themeBalham } from 'ag-grid-community'

import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'

import { buildFutureOptionColumnDefs } from './FutureOptionView_ColumnDefs'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import AddEditFutureOptionView from './AddEditFutureOptionView.vue'
import IdentifierView from '../IdentifierView.vue'

import reference from '@/api/reference'
import security from '@/api/security'
import dayjs from 'dayjs'
import { mapIdentifierField } from '@/utils/secutils'
import debounce from 'lodash/debounce'

// const store = useStore()
const gridApi = ref(null)
const gridOptions = {
  rowSelection: 'single',

  onGridReady: (params) => {
    gridApi.value = params.api
  },
}

const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

defineExpose({
  AgEditCellRenderer,
})

const labelCol = {
  span: 4,
  style: { textAlign: 'left' },
}

const wrapperCol = {
  span: 20,
}
const isFuture = ref(true)
const loading = ref(false)
const rowData = ref([])
const futureInstClassList = ref([])
const optionInstClassList = ref([])
const exchangeList = ref([])
const tickerOptions = ref([])
const tickerList = ref([])
const identifierTypeList = ref([])
const issuerList = ref([])
const identifierRawData = ref({
  securityId: null,
  identifiers: [],
})
const addModalVisible = ref(false)
const addForm = ref({})
const isEdit = ref(false)
const identifierModalVisible = ref(false)

const query = ref({
  assetClass: undefined,
  externalInstClass: undefined,
  exchangeCode: undefined,
  underlying: undefined,
  securityDesc: undefined,
  secId: undefined,
  identType: undefined,
  identifier: undefined,
  effectiveDate: undefined,
})

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

const searchTicker = (ticker) => {
  if (!ticker || ticker.length < 3 || !query.value.identType) {
    return
  }
  const identType = query.value.identType
  security
    .getSimpleSecurities({
      tickerOperator: 'START_WITH',
      identType: identType,
      identifier: ticker,
    })
    .then((res) => {
      const data = Array.isArray(res.data) ? res.data : []
      tickerList.value = data
      const tickers = data
        .filter((item) => {
          return mapIdentifierField(item, identType)
        })
        .map((item) => {
          return mapIdentifierField(item, identType)
        })
      tickers.sort((a, b) => b.localeCompare(a))
      const arr = tickers.length > 8 ? tickers.slice(0, 8) : tickers
      tickerOptions.value = arr.map((item) => ({ value: item }))
    })
}
const onSearchTicker = debounce(
  function (ticker) {
    searchTicker(ticker)
  },
  600,
  { maxWait: 1000 }
)

const onSearch = async () => {
  loading.value = true
  try {
    const params = { ...query.value }
    if (isFuture.value) {
      params.assetClass = 'FUTURE'
    } else {
      params.assetClass = 'OPTION'
    }

    if (!params.identifier || !params.identifier.trim()) {
      params.identType = null
    }
    params.tickerOperator = 'START_WITH'
    const resp = await security.getSimpleSecurities(params)
    const data = resp.data
    rowData.value = Array.isArray(data) ? data : []
    if (rowData.value.length === 0) {
      message.info('No data found.')
    }
  } catch (e) {
    message.error(getErrorMessage(e, 'Query failed'))
  } finally {
    loading.value = false
  }
}

const showAddModal = () => {
  isEdit.value = false
  addForm.value = {
    securityId: undefined,
    externalSecurityId: undefined,
    exchange: {
      exchangeId: undefined,
      exchName: undefined,
      micExchName: undefined,
    },
    issuer: {
      issuerId: undefined,
      shortName: undefined,
      name: undefined,
    },
    underlyingTicker: undefined,
    assetClass: 'FUTURE',
    instrumentClass: undefined,
    isin: undefined,
    ticker: undefined,
    name: undefined,
    desc: undefined,
    priceMultiplier: 1,
    contractSize: 1,
    settlementType: 'C',
    tickSize: 1,
    tickValue: 1,
    pnlCurrency: undefined,
    isActive: true,
    callPut: 'C',
    exerciseStyle: undefined,
    exercisePrice: undefined,
    exercisePriceCurrency: undefined,
    exercisePriceMultiplier: 1,
    leg1: null,
    leg2: null,
  }

  addModalVisible.value = true
}

const showCopyModal = async () => {
  const selectedRows = gridApi.value?.getSelectedRows?.() || []
  if (selectedRows.length !== 1) {
    message.warning('Please select one row to copy')
    return
  }

  const params = { securityId: selectedRows[0].securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      const data = resp.data
      addForm.value = formatDateTime({
        ...data,
        securityId: undefined,
        externalSecurityId: undefined,
        exchange: { ...data.exchange },
        issuer: { ...data.issuer },
        identifiers: data.identifiers ? [...data.identifiers] : [],
        underlying: data.underlying ? { ...data.underlying } : null,

        leg1: data.leg1
          ? {
              ...data.leg1,
              securityId: undefined,
              externalSecurityId: undefined,
              exchange: { ...data.leg1.exchange },
              issuer: { ...data.leg1.issuer },
              underlying: { ...data.leg1.underlying },
              identifiers: data.leg1.identifiers
                ? [...data.leg1.identifiers]
                : [],
            }
          : null,
        leg2: data.leg2
          ? {
              ...data.leg1,
              securityId: undefined,
              externalSecurityId: undefined,
              exchange: { ...data.leg2.exchange },
              issuer: { ...data.leg2.issuer },
              underlying: { ...data.leg2.underlying },
              identifiers: data.leg2.identifiers
                ? [...data.leg2.identifiers]
                : [],
            }
          : null,
      })
      isEdit.value = false
      addModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const formatDateTime = (security) => {
  const format = (val) => (val ? dayjs(dayjs(val).format('YYYY-MM-DD')) : null)

  addForm.value.issueDate = format(security.issueDate)
  security.settleDate = format(security.settleDate)
  security.maturityDate = format(security.maturityDate)
  security.firstTradeDate = format(security.firstTradeDate)
  security.lastTradeDate = format(security.lastTradeDate)
  security.firstNoticeDate = format(security.firstNoticeDate)
  security.firstExerciseDate = format(security.firstExerciseDate)
  security.winStartDate = format(security.winStartDate)
  security.winEndDate = format(security.winEndDate)

  security.expiryTime = security.expiryTime
    ? dayjs(security.expiryTime, 'HH:mm:ss')
    : null

  if (
    security.assetClass === 'OPTION' &&
    security.instrumentClass === 'FX_OP'
  ) {
    if (security.optionType && security.optionType === 'DUALDIGITAL') {
      if (security.leg1) {
        security.leg1.issueDate = format(security.leg1.issueDate)
        security.leg1.settleDate = format(security.leg1.settleDate)
        security.leg1.maturityDate = format(security.leg1.maturityDate)
        security.leg1.firstTradeDate = format(security.leg1.firstTradeDate)
        security.leg1.lastTradeDate = format(security.leg1.lastTradeDate)
        security.leg1.firstNoticeDate = format(security.leg1.firstNoticeDate)
        security.leg1.firstExerciseDate = format(
          security.leg1.firstExerciseDate
        )
        security.leg1.winStartDate = format(security.leg1.winStartDate)
        security.leg1.winEndDate = format(security.leg1.winEndDate)
        security.leg1.expiryTime = security.leg1.expiryTime
          ? dayjs(security.leg1.expiryTime, 'HH:mm:ss')
          : null
      }

      if (security.leg2) {
        security.leg2.issueDate = format(security.leg2.issueDate)
        security.leg2.settleDate = format(security.leg2.settleDate)
        security.leg2.maturityDate = format(security.leg2.maturityDate)
        security.leg2.firstTradeDate = format(security.leg2.firstTradeDate)
        security.leg2.lastTradeDate = format(security.leg2.lastTradeDate)
        security.leg2.firstNoticeDate = format(security.leg2.firstNoticeDate)
        security.leg2.firstExerciseDate = format(
          security.leg2.firstExerciseDate
        )
        security.leg2.winStartDate = format(security.leg2.winStartDate)
        security.leg2.winEndDate = format(security.leg2.winEndDate)
        security.leg2.expiryTime = security.leg2.expiryTime
          ? dayjs(security.leg2.expiryTime, 'HH:mm:ss')
          : null
      }
    }
  }
  return security
}

const showEditModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      addForm.value = formatDateTime(resp.data)
      isEdit.value = true
      addModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const handleUpdateSecurity = async (newSecurity, isEdit) => {
  loading.value = true
  try {
    if (isEdit) {
      await security.updateSec(newSecurity)
      message.success('Security updated successfully')
      return
    }
    await security.createSec(newSecurity)
    message.success('Security created successfully')
  } catch (error) {
    message.error(
      getErrorMessage(
        error,
        isEdit ? 'Failed to update security' : 'Failed to create security'
      )
    )
  } finally {
    loading.value = false
  }
}

const filterOption = (input, option) => {
  const key = option?.key ?? option?.value ?? ''
  return String(key).toLowerCase().includes(input.toLowerCase())
}

const showIdentifierModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    identifierRawData.value.securityId = data.securityId
    identifierRawData.value.identifiers = resp.data.identifiers || []
    identifierModalVisible.value = true
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const columnDefs = computed(() =>
  buildFutureOptionColumnDefs({
    onIdentifiers: showIdentifierModal,
    onEdit: showEditModal,
  })
)

const handleUpdateIdentifiers = () => {
  /* loading.value = true
  security
    .updateIdentifiers(securityId, newIdentifiers)
    .then(() => {
      message.success('Identifiers updated successfully')
    })
    .catch((error) => {
      console.error('Error updating identifiers:', error)
    })
    .finally(() => {
      loading.value = false
    }) */
}

onMounted(async () => {
  try {
    const result = await reference.getReferenceDataV2()
    futureInstClassList.value = result.data?.futureTypes || []
    optionInstClassList.value =
      result.data?.optionTypes.filter((item) => item !== 'WRNT') || []
    exchangeList.value = result.data?.exchanges || []
    identifierTypeList.value = result.data?.identifierTypes || []
    issuerList.value = result.data?.issuers || []
  } catch (e) {
    message.error(getErrorMessage(e, 'Failed to fetch reference lists'))
  }
})

provide('exchangeList', exchangeList)
provide('issuerList', issuerList)
provide('futureInstClassList', futureInstClassList)
provide('optionInstClassList', optionInstClassList)
provide('identifierTypeList', identifierTypeList)
provide('security', security)
</script>
